import {
  OverviewPeriod,
  TypeAttribute,
  TypeAttributeSort,
  TypeCrashSortKey,
  TypeCrashStatus,
  TypeMetricKey,
} from '@trace/types';
import React, { createContext, FunctionComponent, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  ATTRIBUTE_NAME_MAP,
  ATTRIBUTE_SORT_DEFAULT,
  ATTRIBUTE_SORT_ORDER,
} from '../../constants/Attributes';
import {
  CRASH_SORT_DIRECTION_DEFAULT,
  CRASH_SORT_KEY_DEFAULT,
  CRASH_STATUS_DEFAULT,
} from '../../constants/Crashes';
import { METRIC_GROUPING_DEFAULTS } from '../../constants/Metrics';
import { TIME_RANGE_DEFAULT, TypeDuration } from '../../constants/TimeRange';
import { TypeFilterLabel } from '../Filters/useFilters';
import useUrlState, {
  URLStateDecoders,
  URLStateDefaults,
  URLStateEncoders,
  URLStateValidators,
} from './useUrlState';
import isDurationValid from '../../utils/time/isDurationValid';
import isDateRangeValid from '../../utils/time/isTimeRangeValid';

export interface URLState {
  appSlug?: string;
  attributeGroups: { [key in TypeMetricKey]: TypeAttribute };
  attributeSort: TypeAttributeSort;
  crashSortDirection: 'asc' | 'desc';
  crashSortKey: TypeCrashSortKey;
  crashStatus: TypeCrashStatus | 'all';
  dateFrom?: number;
  demo?: boolean;
  dateTo?: number;
  duration: TypeDuration;
  filters: TypeFilterLabel[];
  overviewPeriod: OverviewPeriod;
  session?: string;
  step?: number;
}

type URLStateContextType = URLState & {
  updateUrlState: (state: Partial<URLState>) => void;
  push: (pathname: string) => void;
  replace: (pathname: string) => void;
  search: string;
  to: (pathname: string) => string;
};

const urlStateDefaults: URLStateDefaults<URLState> = {
  attributeGroups: METRIC_GROUPING_DEFAULTS,
  attributeSort: ATTRIBUTE_SORT_DEFAULT,
  crashSortDirection: CRASH_SORT_DIRECTION_DEFAULT,
  crashSortKey: CRASH_SORT_KEY_DEFAULT,
  crashStatus: CRASH_STATUS_DEFAULT,
  demo: false,
  duration: TIME_RANGE_DEFAULT,
  filters: [],
  overviewPeriod: 'daily',
};

const urlStateDecoders: URLStateDecoders<URLState> = {
  attributeGroups: (g) =>
    g
      .split(',')
      .map((group) => group.split(':'))
      .reduce<{ [key in TypeMetricKey]: TypeAttribute }>(
        (group, [k, v]) => ({ ...group, [k]: v }),
        METRIC_GROUPING_DEFAULTS,
      ),
  dateFrom: (v) => parseInt(v, 10),
  dateTo: (v) => parseInt(v, 10),
  demo: (v) => JSON.parse(v),
  filters: (v) =>
    v
      .split(',')
      .map((l) => l.split(':'))
      .filter(([type, value]) => type && value)
      .map(([type, value]) => ({
        type: type as TypeAttribute,
        value,
      })),
};

const urlStateEncoders: URLStateEncoders<URLState> = {
  attributeGroups: (v) =>
    Object.entries(v)
      .filter(([k, value]) => METRIC_GROUPING_DEFAULTS[k as TypeMetricKey] !== value)
      .map(([k, value]) => `${k}:${value}`)
      .toString(),
  filters: (v) => v.map(({ type, value }) => `${type}:${value}`).toString(),
};

const urlStateValidators: URLStateValidators<URLState> = {
  attributeGroups: (v) => Object.values(v).every((value) => ATTRIBUTE_NAME_MAP[value]),
  attributeSort: (v) => ATTRIBUTE_SORT_ORDER.includes(v),
  dateFrom: (v, { dateTo }) => isDateRangeValid(v, dateTo),
  dateTo: (v, { dateFrom }) => isDateRangeValid(dateFrom, v),
  duration: (v) => isDurationValid(v),
  filters: (v) => Array.isArray(v) && v.every(({ type, value }) => type && value),
  step: (v) => v !== undefined && Number.isFinite(v),
};

export const URLStateContext = createContext<URLStateContextType>({
  attributeGroups: METRIC_GROUPING_DEFAULTS,
  attributeSort: ATTRIBUTE_SORT_DEFAULT,
  crashSortDirection: CRASH_SORT_DIRECTION_DEFAULT,
  crashSortKey: CRASH_SORT_KEY_DEFAULT,
  crashStatus: CRASH_STATUS_DEFAULT,
  duration: TIME_RANGE_DEFAULT,
  filters: [],
  updateUrlState: () => undefined,
  overviewPeriod: 'daily',
  push: () => undefined,
  replace: () => undefined,
  search: '',
  to: () => '',
});

const URLState: FunctionComponent = (props) => {
  const history = useHistory();
  const location = useLocation();
  const refSearch = useRef(new URLSearchParams(location.search).toString());

  const onUpdateSearch = useCallback(
    (search: string) => {
      refSearch.current = search;
      history.replace({ search });
    },
    [history],
  );

  const state = useUrlState<URLState>({
    decoders: urlStateDecoders,
    defaults: urlStateDefaults,
    encoders: urlStateEncoders,
    onUpdateSearch,
    search: location.search,
    validators: urlStateValidators,
  });

  const to = useCallback((pathname: string) => `${pathname}?${refSearch.current}`, []);
  const push = useCallback((pathname: string) => history.push(to(pathname)), [history, to]);
  const replace = useCallback((pathname: string) => history.replace(to(pathname)), [history, to]);

  return (
    <URLStateContext.Provider
      {...props}
      value={{
        ...state,
        push,
        replace,
        to,
      }}
    />
  );
};

export default URLState;
